// ============================================================
// TMR Portal Admin — blocos de página reutilizáveis
// ============================================================
import type { CSSProperties, ReactNode } from 'react';
import { Icons, StrokeIcon } from '../lib/icons';
import { OverlayPortal } from './OverlayPortal';

export function PageHeader({
  eyebrow,
  title,
  desc,
  actions,
}: {
  eyebrow?: string;
  title: string;
  desc?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="pa-page-header" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
      <div>
        {eyebrow && <div className="pa-eyebrow">{eyebrow}</div>}
        <h1 className="pa-page-title" style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 27, letterSpacing: '-0.01em', margin: '4px 0 0', lineHeight: 1.05 }}>
          {title}
        </h1>
        {desc && <p className="pa-page-desc" style={{ margin: '6px 0 0', color: 'var(--fg-muted)', fontSize: 14.5, maxWidth: 560 }}>{desc}</p>}
      </div>
      {actions && <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>{actions}</div>}
    </div>
  );
}

// Caixa de busca
export function SearchBox({
  value,
  onChange,
  placeholder,
  width = 280,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  width?: number | string;
}) {
  return (
    <div style={{ position: 'relative', width }}>
      <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--stone-400)', display: 'flex' }}>
        <StrokeIcon d={Icons.search} size={16} />
      </span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="pa-input"
        style={{
          width: '100%',
          padding: '9px 12px 9px 36px',
          fontFamily: 'var(--font-sans)',
          fontSize: 14,
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-sm)',
          background: 'var(--cream-50)',
          color: 'var(--ink-900)',
          outline: 'none',
        }}
      />
    </div>
  );
}

export type FilterTab = string | { value: string; label: string; count?: number | null };

// Abas de filtro (pílulas)
export function FilterTabs({ tabs, value, onChange }: { tabs: FilterTab[]; value: string; onChange: (v: string) => void }) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {tabs.map((t) => {
        const v = typeof t === 'string' ? t : t.value;
        const label = typeof t === 'string' ? t : t.label;
        const count = typeof t === 'object' ? t.count : null;
        const active = v === value;
        return (
          <button
            key={v}
            type="button"
            className="pa-tab"
            data-active={active || undefined}
            onClick={() => onChange(v)}
            style={{
              background: active ? 'var(--forest-900)' : 'var(--cream-50)',
              color: active ? 'var(--cream-50)' : 'var(--fg-muted)',
              border: `1px solid ${active ? 'var(--forest-900)' : 'var(--border-default)'}`,
              borderRadius: 999,
              padding: '7px 14px',
              fontFamily: 'var(--font-sans)',
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all var(--duration-fast) var(--ease-out)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
            }}
          >
            {label}
            {count != null && (
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  padding: '1px 6px',
                  borderRadius: 999,
                  background: active ? 'rgba(255,255,255,0.16)' : 'var(--cream-200)',
                  color: active ? 'var(--cream-50)' : 'var(--fg-subtle)',
                }}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export interface TableColumn {
  label: string;
  align?: CSSProperties['textAlign'];
  width?: number;
}

// Tabela genérica
export function Table({ columns, children, dense }: { columns: TableColumn[]; children: ReactNode; dense?: boolean }) {
  return (
    <div className="pa-card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
          <thead>
            <tr>
              {columns.map((c, i) => (
                <th
                  key={i}
                  className="pa-th"
                  style={{
                    textAlign: c.align || 'left',
                    padding: dense ? '11px 14px' : '14px 18px',
                    fontFamily: 'var(--font-sans)',
                    fontSize: 11.5,
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: 'var(--fg-subtle)',
                    borderBottom: '1px solid var(--border-default)',
                    background: 'var(--cream-100)',
                    whiteSpace: 'nowrap',
                    width: c.width,
                  }}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </div>
  );
}

export function Td({
  children,
  align,
  dense,
  style,
}: {
  children: ReactNode;
  align?: CSSProperties['textAlign'];
  dense?: boolean;
  style?: CSSProperties;
}) {
  return (
    <td
      style={{
        padding: dense ? '11px 14px' : '14px 18px',
        textAlign: align || 'left',
        fontFamily: 'var(--font-sans)',
        fontSize: 14,
        color: 'var(--ink-900)',
        borderBottom: '1px solid var(--border-default)',
        verticalAlign: 'middle',
        ...style,
      }}
    >
      {children}
    </td>
  );
}

// Selo de método de pagamento
export function MethodTag({ method }: { method: string }) {
  const map: Record<string, { label: string; icon: ReactNode }> = {
    boleto: { label: 'Boleto', icon: Icons.receipt },
    pix: { label: 'Pix', icon: Icons.pix },
    antecipacao: { label: 'Antecipação', icon: Icons.zap },
    cartao: { label: 'Cartão', icon: Icons.card },
  };
  const m = map[method] || map.boleto;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13.5, color: 'var(--fg-default)' }}>
      <span style={{ color: 'var(--forest-700)', display: 'flex' }}>
        <StrokeIcon d={m.icon} size={15} />
      </span>
      {m.label}
    </span>
  );
}

// Drawer lateral — drawn at the end of the page by OverlayPortal, so the top bar never covers it. Above the
// sidebar (z 50) like every overlay that blocks the page, and below the dialogs (70–90) opened from inside it.
export function Drawer({
  open,
  onClose,
  title,
  children,
  footer,
  width = 460,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}) {
  return (
    <OverlayPortal>
      <div
        className="pa-drawer-scrim"
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(28,30,28,0.32)',
          zIndex: 60,
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity var(--duration-base) var(--ease-out)',
        }}
      />
      <div
        className="pa-drawer"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width,
          maxWidth: '92vw',
          zIndex: 61,
          background: 'var(--cream-50)',
          boxShadow: 'var(--shadow-xl)',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform var(--duration-slow) var(--ease-out)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid var(--border-default)' }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 19, margin: 0, letterSpacing: '-0.01em' }}>{title}</h3>
          <button type="button" onClick={onClose} className="pa-icon-btn" style={{ width: 34, height: 34 }}>
            <StrokeIcon d={Icons.x} size={18} />
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>{children}</div>
        {footer && <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border-default)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>{footer}</div>}
      </div>
    </OverlayPortal>
  );
}

// Linha rótulo/valor para drawers e cartões
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 16, padding: '11px 0', borderBottom: '1px solid var(--border-default)' }}>
      <span style={{ fontSize: 13, color: 'var(--fg-muted)' }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink-900)', textAlign: 'right' }}>{children}</span>
    </div>
  );
}

// Estado vazio reutilizável
export function EmptyState({ icon, title, desc }: { icon: ReactNode; title: string; desc?: string }) {
  return (
    <div style={{ textAlign: 'center', padding: '56px 24px', color: 'var(--fg-subtle)' }}>
      <div style={{ display: 'inline-flex', color: 'var(--stone-400)', marginBottom: 12 }}>
        <StrokeIcon d={icon} size={32} />
      </div>
      <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--fg-muted)' }}>{title}</div>
      {desc && <div style={{ fontSize: 13.5, marginTop: 4 }}>{desc}</div>}
    </div>
  );
}
